import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getOrgList, getSubscrtiptionData } from '../../apiBridge';
import { OrganisationInfo, SubscriptionInfo } from '../../interface';
import { useAsyncCache } from './useCache';

type SubscriptionFeature = {
  name: string;
  options: Record<string, any>
  expired_at: string | null;
}

interface OrganizationContextType {
  organization: OrganisationInfo | null;
  subscription: SubscriptionInfo | null;
  subscriptionFeatures: SubscriptionFeature[];
  loading: boolean;
  refreshOrganization: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<OrganisationInfo | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState<SubscriptionFeature[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  const orgCache = useAsyncCache<OrganisationInfo[]>();
  const subCache = useAsyncCache<{ subscription: SubscriptionInfo; features: SubscriptionFeature[] }>();

  const fetchOrganizationList = async (): Promise<OrganisationInfo[]> => {
    const result = await orgCache.fetchWithCache(
      'organizations',
      async () => {
        const orgResponse = await getOrgList();
        if (orgResponse.status === 200) {
          const orgData = await orgResponse.json();
          return orgData.organizations || [];
        }
        throw new Error('Failed to fetch organizations');
      },
      'org list'
    );
    
    return result || [];
  };

  const fetchSubscriptionData = async (orgId: string) => {
    const result = await subCache.fetchWithCache(
      `subscription-${orgId}`,
      async () => {
        const subResponse = await getSubscrtiptionData(orgId);
        if (subResponse.status === 200) {
          const subData = await subResponse.json();
          return {
            subscription: subData.subscriptions,
            features: subData.subscription_features
          };
        }
        throw new Error('Failed to fetch subscription data');
      },
      'subscription'
    );
    
    if (result) {
      setSubscription(result.subscription);
      setSubscriptionFeatures(result.features);
    }
  };

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      
      // 使用缓存机制获取组织列表
      const organizations = await fetchOrganizationList();
      
      if (organizations.length > 0) {
        const firstOrg = organizations[0];
        setOrganization(firstOrg);

        // 使用缓存机制获取subscription数据
        await fetchSubscriptionData(firstOrg.ID);
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshOrganization = async () => {
    await fetchOrganizationData();
  };

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const value: OrganizationContextType = {
    organization,
    subscription,
    subscriptionFeatures,
    loading,
    refreshOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};